import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import InputGroup from "./InputGroup";
import Select from "./Select";
import { handleFormikValueChange } from "../../utils";

type TreeOption = {
  id: number | string;
  name: string;
  parentId?: number | string | null;
  tenantName?: string;
  tenantPublic?: boolean;
  [key: string]: unknown;
};

type TreeOptionInternal = TreeOption & {
  depth: number;
  isParent: boolean;
};

type OwnProps = {
  readonly name?: string;
  readonly disabled?: boolean;
  readonly isSubmitting?: boolean;
  readonly values?: $TSFixMe;
  readonly options?: TreeOption[];
  readonly onChange?: $TSFixMeFunction;
  readonly input?: $TSFixMe;
  readonly [key: string]: unknown;
};

function buildFlatTree(options: TreeOption[]): TreeOptionInternal[] {
  const roots = options.filter((o) => o.parentId == null);
  const childrenMap = new Map<number | string, TreeOption[]>();

  options.forEach((opt) => {
    if (opt.parentId != null) {
      const siblings = childrenMap.get(opt.parentId) ?? [];
      siblings.push(opt);
      childrenMap.set(opt.parentId, siblings);
    }
  });

  const result: TreeOptionInternal[] = [];

  function walk(node: TreeOption, depth: number) {
    const isParent = childrenMap.has(node.id);
    result.push({ ...node, depth, isParent });
    const children = childrenMap.get(node.id) ?? [];
    children.sort((a, b) => a.name.localeCompare(b.name));
    children.forEach((child) => walk(child, depth + 1));
  }

  roots.sort((a, b) => a.name.localeCompare(b.name));
  roots.forEach((root) => walk(root, 0));

  return result;
}

/** Collects all ancestor IDs of a given option so their subtrees are expanded. */
function getAncestorIds(
  option: TreeOption | null | undefined,
  allOptions: TreeOption[],
): (number | string)[] {
  if (option?.parentId == null) return [];
  const parent = allOptions.find((o) => o.id === option.parentId);
  return [option.parentId, ...getAncestorIds(parent, allOptions)];
}

function isVisible(
  opt: TreeOptionInternal,
  flatTree: TreeOptionInternal[],
  expandedIds: (number | string)[],
): boolean {
  if (opt.parentId == null) return true;
  const { parentId } = opt;
  if (!expandedIds.includes(parentId)) return false;
  // parent is always defined here: buildFlatTree only adds nodes reachable from roots,
  // so every node with a parentId has its parent present in flatTree as well.
  const parent = flatTree.find((o) => o.id === parentId)!;
  return isVisible(parent, flatTree, expandedIds);
}

function getSelectedValue(values: $TSFixMe, name: string | undefined) {
  if (!values) return undefined;
  return name?.split(".").reduce((p: $TSFixMe, c: string) => p?.[c], values);
}

function SelectTree(props: OwnProps) {
  const {
    name,
    values,
    disabled,
    isSubmitting,
    input,
    options,
    onChange,
    ...rest
  } = props;

  const fullTree = useMemo(() => buildFlatTree(options ?? []), [options]);

  const [expandedIds, setExpandedIds] = useState<(number | string)[]>(() =>
    getAncestorIds(getSelectedValue(values, name), fullTree),
  );

  const prevSelectedIdRef = useRef<number | string | undefined>(
    getSelectedValue(values, name)?.id,
  );

  useEffect(() => {
    const selected = getSelectedValue(values, name);
    const nextId = selected?.id;
    if (nextId != null && nextId !== prevSelectedIdRef.current) {
      const ancestorIds = getAncestorIds(selected, fullTree);
      if (ancestorIds.length > 0) {
        setExpandedIds((prev) =>
          Array.from(new Set([...prev, ...ancestorIds])),
        );
      }
      prevSelectedIdRef.current = nextId;
    }
  }, [values, name, fullTree]);

  const toggleExpanded = useCallback((id: number | string) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((eid) => eid !== id) : [...prev, id],
    );
  }, []);

  const handleToggleExpand = useCallback(
    (id: number | string, event: $TSFixMe) => {
      event.stopPropagation();
      event.preventDefault();
      toggleExpanded(id);
    },
    [toggleExpanded],
  );

  const handleChange = useCallback(
    (value: TreeOptionInternal | null) => {
      if (onChange) {
        onChange(value);
      } else {
        handleFormikValueChange(props, value);
      }
    },
    [onChange],
  );

  const renderOption = useCallback(
    (option: TreeOptionInternal) => {
      const isExpanded = expandedIds.includes(option.id);
      const { depth } = option;

      const verified =
        option.tenantName && option.tenantPublic ? (
          <span className="select-option-icon">
            <span className="icon icon3-eh-radio-button-checked" />
          </span>
        ) : null;

      const tenant =
        option.tenantName && !option.tenantPublic
          ? ` (${option.tenantName})`
          : null;

      const indicator = option.isParent ? (
        <button
          type="button"
          style={{
            marginRight: 6,
            fontSize: 10,
            cursor: "pointer",
            display: "inline-block",
            userSelect: "none",
            minWidth: 14,
            padding: 0,
            border: "none",
            background: "none",
          }}
          onMouseDown={(e) => handleToggleExpand(option.id, e)}
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? "▼" : "▶"}
        </button>
      ) : (
        <span style={{ display: "inline-block", minWidth: 20 }} />
      );

      const nameNode = (
        <span className="select-option-value">{option.name}</span>
      );

      return (
        <div style={{ paddingLeft: depth * 16 }}>
          {indicator}
          {nameNode}
          {tenant}
          {verified}
        </div>
      );
    },
    [expandedIds, handleToggleExpand],
  );

  const renderSelectedDisplay = useCallback((option: TreeOptionInternal) => {
    const verified =
      option.tenantName && option.tenantPublic ? (
        <span className="select-option-icon">
          <span className="icon icon3-eh-radio-button-checked" />
        </span>
      ) : null;

    const tenant =
      option.tenantName && !option.tenantPublic
        ? ` (${option.tenantName})`
        : null;

    return (
      <div>
        <span className="select-option-value">{option.name}</span>
        {tenant}
        {verified}
      </div>
    );
  }, []);

  const visibleOptions = useMemo(
    () => fullTree.filter((opt) => isVisible(opt, fullTree, expandedIds)),
    [fullTree, expandedIds],
  );

  const selectedOption = useMemo(
    () => getSelectedValue(values, name),
    [values, name],
  );

  const sharedSelectProps = {
    ...rest,
    name,
    options: visibleOptions,
    disabled: disabled || isSubmitting,
    renderOption,
    renderSelectedValue: renderSelectedDisplay,
    disableOptionsSort: true,
  };

  if (input) {
    return (
      <InputGroup {...rest} name={name}>
        <Select
          {...sharedSelectProps}
          onChange={input.onChange}
          selectedOption={selectedOption}
        />
      </InputGroup>
    );
  }

  return (
    <InputGroup {...rest} name={name}>
      <Select
        {...sharedSelectProps}
        onChange={handleChange}
        selectedOption={selectedOption}
      />
    </InputGroup>
  );
}

export default SelectTree;
